import type { RouteConfig } from '@hs/shared';
import { Navigate } from 'react-router-dom';
import { AppShell } from '@/components/layouts/app';
import HomePage from '@/pages/home';
import AdminPage from '@/pages/admin';
import MyPage from '@/pages/my';
import ForbiddenPage from '@/pages/common/ForbiddenPage';
import BizPage from '@/pages/biz';
import AiPage from '@/pages/ai';
import SpacesManagementPage from '@/pages/spaces';
import UserManagementPage from '@/pages/users';
import WechatPage from '@/pages/wechat';
import CustomerServicePage from '@/pages/wechat/pages/CustomerServicePage';
import MenuManagementPage from '@/pages/wechat/pages/MenuManagementPage';
import AutoReplyPage from '@/pages/wechat/pages/AutoReplyPage';
import ContactsPage from '@/pages/contacts';
import ActivitiesPage from '@/pages/activities';
import ActivityDetailShell from '@/pages/activities/detail';
import ActivityStatsDashboard from '@/pages/activities/detail/stats';
import ActivityLotteryDashboard from '@/pages/activities/detail/lottery';
import InvitationLeaderboardPage from '@/pages/activities/detail/leaderboard';
import ActivityWinnersPage from '@/pages/activities/detail/winners';
import KnowledgeBasesLayout from '@/pages/knowledge-bases/layout';
import KnowledgeBasesPage from '@/pages/knowledge-bases';
import KnowledgeBasesListPage from '@/pages/knowledge-bases/list';
import { DocPage } from '@/pages/knowledge-bases/DocPage';
import { SlidesPage } from '@/pages/knowledge-bases/slides';
import PostersLayout from '@/pages/posters/layout';
import PostersPage from '@/pages/posters';
import { PosterCanvasPage } from '@/pages/posters/canvas';
import DisplaysLayout from '@/pages/display/layout';
import DisplaysPage from '@/pages/display';
import { DisplayCanvasPage } from '@/pages/display/canvas';
import AssetsPage from '@/pages/assets';
import QnairesPage from '@/pages/qnaires';
import TestAiPage from '@/pages/test-ai';

/**
 * App 应用路由配置
 * 
 * 特性：
 * - 使用 meta.requiresAuth 声明是否需要认证
 * - 路由层只做认证检查，不做权限检查
 * - 页面内的权限控制使用 PermissionGuard 或 usePermission
 * - 所有页面都在 AppShell（HeroUI Pro）下
 */
export const appRoutes: RouteConfig[] = [
  {
    path: '/',
    element: <AppShell />,
    meta: {
      title: '应用端',
      requiresAuth: true, // 与 admin、mobile 一致：路由层统一做认证，未登录由 RouteGuard 跳 home SSO
    },
    children: [
      {
        path: '',
        index: true,
        element: <HomePage />,
        meta: {
          title: '首页',
        },
      },
      {
        path: 'home',
        element: <HomePage />,
        meta: {
          title: '首页',
        },
      },
      {
        path: 'forbidden',
        element: <ForbiddenPage />,
        meta: {
          title: '无权限',
          requiresAuth: false,
        },
      },
      {
        path: 'biz',
        element: <BizPage />,
        meta: {
          title: '业务管理',
        },
      },
      {
        path: 'ai',
        element: <AiPage />,
        meta: {
          title: 'AI 管理',
          requiredRoles: ['PLATFORM.OWNER'],
        },
      },
      {
        path: 'spaces',
        element: <SpacesManagementPage />,
        meta: {
          title: '空间管理',
        },
      },
      {
        path: 'users',
        element: <UserManagementPage />,
        meta: {
          title: '用户管理',
        },
      },
      {
        path: 'contacts',
        element: <ContactsPage />,
        meta: {
          title: '联系人',
        },
      },
      {
        path: 'knowledge-bases',
        element: <KnowledgeBasesLayout />,
        meta: {
          title: '知识库',
        },
        children: [
          {
            path: '',
            index: true,
            element: <KnowledgeBasesPage />,
            meta: {
              title: '知识库',
            },
          },
          {
            path: 'doc/:docId',
            element: <DocPage />,
            meta: {
              title: '文档编辑',
            },
          },
          {
            path: 'slides/:slideId/play',
            element: <SlidesPage />,
            meta: {
              title: '播放幻灯片',
            },
          },
          {
            path: 'slides/:slideId',
            element: <SlidesPage />,
            meta: {
              title: '编辑幻灯片',
            },
          },
          {
            path: 'list',
            element: <KnowledgeBasesListPage />,
            meta: {
              title: '知识库列表',
            },
          },
        ],
      },
      {
        path: 'posters',
        element: <PostersLayout />,
        meta: {
          title: '海报',
        },
        children: [
          {
            path: '',
            index: true,
            element: <PostersPage />,
            meta: {
              title: '海报',
            },
          },
          {
            path: 'canvas/template/:templateId',
            element: <PosterCanvasPage />,
            meta: {
              title: '从模板新建海报',
            },
          },
          {
            path: 'canvas/:posterId/play',
            element: <PosterCanvasPage />,
            meta: {
              title: '播放海报',
            },
          },
          {
            path: 'canvas/:posterId',
            element: <PosterCanvasPage />,
            meta: {
              title: '编辑海报',
            },
          },
        ],
      },
      {
        path: 'displays',
        element: <DisplaysLayout />,
        meta: {
          title: '大屏',
        },
        children: [
          {
            path: '',
            index: true,
            element: <DisplaysPage />,
            meta: {
              title: '大屏',
            },
          },
          {
            path: 'canvas/template/:templateId',
            element: <DisplayCanvasPage />,
            meta: {
              title: '从模板新建大屏',
            },
          },
          {
            path: 'canvas/:displayId/play',
            element: <DisplayCanvasPage />,
            meta: {
              title: '播放大屏',
            },
          },
          {
            path: 'canvas/:displayId',
            element: <DisplayCanvasPage />,
            meta: {
              title: '编辑大屏',
            },
          },
        ],
      },
      {
        path: 'assets',
        element: <AssetsPage />,
        meta: {
          title: '资产库',
        },
      },
      {
        path: 'qnaires',
        element: <QnairesPage />,
        meta: {
          title: '问卷',
        },
      },
      {
        path: 'test-ai',
        element: <TestAiPage />,
        meta: {
          title: 'AI 测试',
        },
      },
      {
        path: 'my',
        element: <MyPage />,
        meta: {
          title: '我的',
        },
      },
      {
        path: 'activities',
        element: <ActivitiesPage />,
        meta: {
          title: '活动',
        },
        children: [
          {
            path: ':id',
            element: <ActivityDetailShell />,
            meta: { title: '活动详情' },
            children: [
              {
                path: '',
                index: true,
                element: <Navigate to="stats" replace />,
                meta: { title: '活动详情' },
              },
              {
                path: 'stats',
                element: <ActivityStatsDashboard />,
                meta: { title: '活动统计大屏' },
              },
              {
                path: 'lottery',
                element: <ActivityLotteryDashboard />,
                meta: { title: '抽奖大屏' },
              },
              {
                path: 'leaderboard',
                element: <InvitationLeaderboardPage />,
                meta: { title: '邀请排行榜' },
              },
              {
                path: 'winners',
                element: <ActivityWinnersPage />,
                meta: { title: '中奖名单' },
              },
            ],
          },
        ],
      },
      {
        path: 'wechat',
        element: <WechatPage />,
        meta: {
          title: '微信管理',
        },
        children: [
          {
            path: '',
            index: true,
            element: <CustomerServicePage />,
            meta: {
              title: '客服消息',
            },
          },
          {
            path: 'customer-service',
            element: <CustomerServicePage />,
            meta: {
              title: '客服消息',
            },
          },
          {
            path: 'menu-management',
            element: <MenuManagementPage />,
            meta: {
              title: '菜单管理',
            },
          },
          {
            path: 'auto-reply',
            element: <AutoReplyPage />,
            meta: {
              title: '自动回复',
            },
          },
        ],
      },
      {
        path: 'admin',
        element: <AdminPage />,
        meta: {
          title: '空间管理',
        },
      },
    ],
  },
];

